import { BufferModes } from '../../enums/buffer-modes';
import { Vec3 } from '../vec';
import { PcvizBaseSceneView, PcvizJobSiteSceneView, PcvizSceneView, PcvizViewer } from './application';
import { PcvizFeature, PcvizGeometry, PcvizJobSite, PcvizLayer, PcvizProject, PcvizWorkspaceElement } from './gis';
import { PcvizJobSiteMapView } from './map';
import { PcvizNode } from './renderer';

export interface PcvizTools {
    SettingsTool: PcvizSettingsToolConstructor;
    InformationTool: PcvizInformationToolConstructor;
    DistanceTool: PcvizDistanceToolConstructor;
    AreaTool: PcvizAreaToolConstructor;
    ElevationTool: PcvizElevationToolConstructor;
    LayerEditionTool: PcvizLayerEditionToolConstructor;
    PointEditionTool: PcvizPointEditionToolConstructor;
    PaintTool: PcvizPaintToolConstructor;
    AnnotationTool: PcvizAnnotationToolConstructor;
    BufferTool: PcvizBufferTool | PcvizBufferToolConstructor;
    ScreenshotTool: PcvizScreenshotToolConstructor;
    SaveViewTool: PcvizSaveViewToolConstructor;
    VirtualTourTool: PcvizVirtualTourToolConstructor;
    ProjectEditionTool: PcvizProjectEditionToolConstructor;
    ReferencePlanesTool: PcvizReferencePlanesToolConstructor;
    SidesTool: PcvizSidesToolConstructor;
    SETTINGS: string;
    INFORMATION: string;
    MEASURE: string;
    CLASSIFICATION: string;
    LAYER_EDITION: string;
    ANNOTATION: string;
    PROJECT_EDITION: string;
    BUFFER: string;
    VIEWER_CONFIGURATION: string;
    SCREENSHOT: string;
    SAVEVIEW: string;
    FINDIMAGES: string;
    LAYER_TRANSFORM: string;
    CURSOR: string;
    POINT_EDITION: string;
    POINT_SELECTION_MODE: {
        QUAD: number;
        POLYGON: number;
    };
    POINT_SELECTION_OPERATION_MODE: {
        REPLACE: number;
        ADD: number;
        SUBSTRACT: number;
        INTERSECT: number;
    };
    REFERENCE_PLANES: string;
    REFERENCE_PLANE_TYPES: ToolReferencePlane;
    REFERENCE_PLANE_COLOR: Float64Array;
}

export type ToolReferencePlane = {
    POLYGON: ToolReferencePlaneValues.POLYGON;
    RECTANGLE_POLYGON: ToolReferencePlaneValues.RECTANGLE_POLYGON;
    FIT_PICKS: ToolReferencePlaneValues.FIT_PICKS;
    ELEVATED: ToolReferencePlaneValues.ELEVATED;
    VERTICAL_FIT_PICKS: ToolReferencePlaneValues.VERTICAL_FIT_PICKS;
};

export enum ToolReferencePlaneValues {
    POLYGON = 0,
    RECTANGLE_POLYGON,
    FIT_PICKS,
    ELEVATED,
    VERTICAL_FIT_PICKS,
}

export declare class PcvizPaintTool {
    constructor(idDiv: string, width: number, height: number, img: string, defaultLabel: string);

    redoList: string[];
    undoList: string[];
    currentActionForPaint: PcvizActionPaint;
    lineWidth: number;
    textSize: number;
    color: string;
    text: string;
    canvas: HTMLCanvasElement;
    setImage(img: string): void;
    isActionText(): boolean;
    undo(): void;
    redo(): void;
    downloadImage(name: string): void;
}

export type PcvizPaintToolConstructor = {
    new (idDiv: string, width: number, height: number, img: string, defaultLabel: string): PcvizPaintTool;
};

export interface PcvizActionPaint {
    id: string;
}

export interface PcvizTool {
    getViewer(): PcvizViewer;
    getProject(): PcvizProject;
    setProject(project: PcvizProject): void;
    setJobSite(jobSite: PcvizJobSite): void;
    getJobSite(): PcvizJobSite;
    isToolActive(): boolean;
    setActive(active: boolean): void;
    setAsReferencePlane(flag: boolean): void;
    listenData(callback: (data: number) => void): void;
    setUpEventListeners(): void;
}

export interface PcvizPickTool extends PcvizTool {
    removeEventListeners(): void;
    overwritePickingColor(color: string): void;
    clearOverwrittenPickingColor(): void;
    _hit: PcvizHit;
}

export interface PcvizHit {
    _backface: boolean;
    _distance: number;
    _drawable: PcvizGeometry;
    _intersectionPoints: Vec3[];
    _localIntersectionPoint: Vec3;
    _matrix: Float64Array;
    _maxDistance: number;
    _nodePath: PcvizNode[];
    _numIntersectionPoints: number;
    _primitiveIndex: number;
    _ratio: number;
    _screenDistance: number;
}

export interface PcvizSettingsTool extends PcvizTool {
    initializePropertySet(): void;
    isDirty(): boolean;
}

export type PcvizSettingsToolConstructor = {
    new (): PcvizSettingsTool;
};

export type PcvizProjectEditionToolDeletedLayersInfo = { id: number; projectsCount: number };

export interface PcvizProjectEditionTool extends PcvizTool {
    //Todo: Paramters are a draft, add correct parameter types
    createVectorLayer(
        layerName: string,
        geomType: string,
        categoryId?: number,
        color?: string,
        automaticAttributes?: boolean,
        //Todo: set as any instead of Property because a problem with import { Property } from '@mirage-projects/cw-ui-components';
        // A task has been created to fix this problem
        // Maybe is related to missing index.d.ts in folders inside cw-ui-components
        previousCategoryAttrs?: Record<string, any>,
        wksParentId?: number,
        coordinateSystem?: string,
        description?: string,
        isTargetCat?: boolean
    ): Promise<PcvizLayer>;
    deleteWorkspaceLayer(id: number): Promise<PcvizProjectEditionToolDeletedLayersInfo[]>;
    deleteLayer(
        id: number,
        deleteSourceLayer?: boolean,
        removeFromWorkspace?: PcvizWorkspaceElement,
        removeDataOnly?: boolean
    ): Promise<void | { dataPaths: string[] }>;
    addLayer(layerId: number, parentId?: number): Promise<void>;
}

export type PcvizProjectEditionToolConstructor = {
    new (): PcvizProjectEditionTool;
};

export interface PcvizInformationTool extends PcvizPickTool {
    getFeatureSelected(): PcvizFeature;
    setSelectedFeature(feature: PcvizFeature, userData?: any, keepManipulatorPos?: boolean): Promise<void>;
    getFeature(layer: PcvizLayer, featureId: number): Promise<PcvizFeature>;
    setImageManipulatorPosition(viewDir?: any, feature?: PcvizFeature): void;
    getSelectedLayer(): PcvizLayer;
    setSelectedLayer(layer?: PcvizLayer | null): void;
    enableContinuousMode(): void;
    disableContinuousMode(): void;
    disableSelect(): void;
    enableSelect(): void;
    clearTool(setSelectionIfLayer?: boolean): void;
    clearPreviousLayer(): void;
    registerListenerScreenData(funct: any): void;
    isMultipleFeatureEnabled(): boolean;
    setClearSelection(mode: boolean): void;
    enableStorePointFeaturePosition(): void;
    disableStorePointFeaturePosition(): void;
    enableMultipleFeatureSelection(): void;
    getPositionAndNormal(): { position: Vec3; normal: Vec3 };
    setStoreAllHits(flag: boolean): void;
}

export type PcvizInformationToolConstructor = {
    new (view: PcvizBaseSceneView): PcvizInformationTool;
};

export interface PcvizDistanceTool extends PcvizPickTool {
    _currentHitPosition: any[];
    _typeEvent: string;
    _positionsArray: any[];
    _lastHitPosition: any[];
    data: number;
    clearTool(): void;
    getMeasurePoints(): any;
    doEscAction(): void;
    setAnglesUnit(showInRadians: boolean): number;
    getAnglesUnit(arg0: string): number;
    switchShowAngles(): void;
    getVertiDistance(): number;
    getHorizDistance(): number;
    getComponentLines(positions: any, vertical: any): any[];
    switchShowHorizAndVerti(): void;
    setHorizLabel(labelTranslated: string): void;
    setVertiLabel(labelTranslated: string): void;
    setDistanceLabel(labelTranslated: string): void;
    repaintTextNode(): void;
}

export type PcvizDistanceToolConstructor = {
    new (viewer: PcvizViewer): PcvizDistanceTool;
};

export interface PcvizAreaTool extends PcvizPickTool {
    _typeEvent: string;
    _positionsArray: any[];
    computePerimeter(): number;
    clearTool(): void;
    isFlatProjection(): boolean;
    getZMax(): number;
    getMeasurePoints(): any;
    toggleProjection(): void;
}

export type PcvizAreaToolConstructor = {
    new (viewer: PcvizViewer): PcvizAreaTool;
};

export interface PcvizElevationTool extends PcvizPickTool {
    changeReferenceLayer(layer: PcvizLayer | null): void;
    setContinuousMode(enable: boolean): void;
    isContinuousMode(): boolean;
}

export type PcvizElevationToolConstructor = {
    new (viewer: PcvizViewer): PcvizElevationTool;
};

export interface PcvizLayerEditionTool extends PcvizPickTool {
    _positionsArray: any[];
    setActive(active: boolean, enableDispatch?: boolean): void;
    getFeatureSelected(): PcvizFeature | undefined;
    setSelectedFeature(feature?: PcvizFeature): void;
    setLayerSelected(layer?: PcvizLayer): void;
    clearTemporaryFeatures(onlySketchTool?: boolean): void;
    enableAddPointMode(): void;
    disableAddPointMode(): void;
    getLayerSelected(): PcvizLayer;
    editGeometrySketch(): void;
    createFeature(): PcvizFeature;
    createPoint(point: any): void;
    addPoint(point: any): void;
    insertFeature(): Promise<void>;
    updateFeature(): Promise<void>;
    deleteFeature(): Promise<void>;
    setSelectedVertex(vertex: number[]): void;
    isGeometryDirty(): boolean;
    getView(): PcvizSceneView;
    removePoint(): void;
    fillTargetAttributes(targetType: string, transformMatrix: any): void;
}

export type PcvizLayerEditionToolConstructor = {
    new (view: PcvizSceneView): PcvizLayerEditionTool;
};

export interface PcvizPointEditionTool extends PcvizPickTool {
    listenersOn: boolean;
    hideViewButtons: boolean;
    applySelection(): void;
    saveModifiedPointSelection(): void;
    isModifyingVertexes(): boolean;
    travel3DSelection(): void;
    downloadInsidersDB(): void;
    setRefreshCallback(callback: () => void): void;
    restoreSectionInsider(): void;
    toggleCapturePoints(mode: number): void;
    cleanPointSelected(): void;
    hideSelectedPoints(): void;
    showHiddenPoints(): void;
    deleteSelectedPoints(): void;
    saveInsidersDB(): void;
    restoreDeletedPoints(): void;
    cleanInsidersDB(): void;
    setOP(mode: number): void;
    getOP(): number;
    addSelectAll(): void;
    addInvertSelection(): void;
    getSelectionMode(): number;
    haveSelectionInsiders(): boolean;
    haveHiddenInsiders(): boolean;
    haveDeletedInsiders(): boolean;
    setScansList(id?: string[]): void;
    setCleanPointsAfterDeactive(value: boolean): void;
    getSelectionProcess(isRemoveHiddenPoints?: boolean): { selections: PcvizInsiderObject[] };
    updateSelectionsVisibility(): void;
    setOnlySection(section: boolean): void;
    setDepthValue(value: number): void;
    modifyRangePointSelection(): void;
    isSectionView(): boolean;
    get3DSelectionExtent(): number[] | undefined;
    getMaximumDepthValue(): number;
    checkOrRemoveInvalidSelectionInsider(): void;
}

export type PcvizPointEditionToolConstructor = {
    new (view: PcvizSceneView): PcvizPointEditionTool;
};

export interface PcvizInsiderObject {
    farPoints: Vec3;
    nearPoints: Vec3;
    scansAffected: PcvizPointEditionScanAffected[];
    type: string;
}

export interface PcvizPointEditionScanAffected {
    idScan: string;
    transform: number[];
}

export interface PcvizAnnotationTool extends PcvizLayerEditionTool {
    doEscapeAction(): void;
}

export type PcvizAnnotationToolConstructor = {
    new (view: PcvizSceneView): PcvizAnnotationTool;
};

export interface PcvizBufferToolCrossSectionPos {
    calculated: number;
    options: {
        min: number;
        minTransformed: number;
        max: number;
        maxTransformed: number;
    };
    value: number;
}

export interface PcvizBufferTool extends PcvizPickTool {
    setMode(mode: BufferModes): void;
    getMode(): BufferModes;
    update(): void;
    setRange(valueRange: number): void;
    setAngle(valueAngle: number): void;
    setCrsParams(params: any): void;
    setSectionRefreshCallback(funct: (comesFromDblClick: boolean, resetZoom: boolean) => void): void;
    setOnlyBuffer(value: boolean): void;
    setBaseColorBuffer(value: boolean): void;
    getIsPathValid(): boolean;
    setData(data: any, type: number): void;
    setUseRegionCube(use: boolean): void;
    setMinColor(color: string): void;
    setMaxColor(color: string): void;
    getCalculateRange(): number;
    getRangeTransformed(value: number): number;
    setCalculateRange(value: number): void;
    getCrossSectionPositionsLength(): number;
    getCrossSectionPositionTransformed(value: number): number;
    getCrossSectionProfile(position: number, collectPoints: boolean): any[];
    getCrossSectionProfile3D(position: number, collectPoints: boolean): any[];
    getReferencePoints(): Float32Array[];
    getLongSectionProfile(collectPoints: boolean): any[];
    getLongSectionProfile3D(collectPoints: boolean): any[];
    getPoints(): any[];
    setCrossSectionPosition(value: number): void;
    setCalculateCrossSectionDepth(value: number): void;
    getCalculateCrossSectionDepth(): number;
    getCrossSectionDepthTransformed(value: number): number;
    getSelectedFeatureGeometryType(): number;
    clearBuffer(avoidClearRegion?: boolean, resetMode?: boolean): void;
    drawBuffer(feature?: PcvizFeature): void;
    setDisabledModes(bufferModesList: BufferModes[]): void;
    getDisabledModes(): BufferModes[];
    setCreateBufferEnabled(isSelected: boolean): void;
    getCrossSectionPosition(): number;

    ON_POINTCLOUDS: number;
    ON_3DMODELS: number;
    ON_FEATURES: number;
    ON_SUPPORTED_TYPES: number;
}

export type PcvizBufferToolConstructor = {
    new (viewer: PcvizViewer): PcvizBufferTool;
};

export interface PcvizImage {
    image: string;
}

export interface PcvizScreenshotTool extends PcvizTool {
    _layer: PcvizLayer;
    setCaptureFinishCallback(funct: () => void): void;
    getCapture(): PcvizImage;
    captureScreen(): void;
    captureLayer(layer?: PcvizLayer): void;
    captureLayerType(layerType: string): void;
    uploadAndUpdateThumbnailToLayer(img: string): Promise<void>;
    uploadAndUpdateThumbnail(img: string): Promise<void>;
}

export type PcvizScreenshotToolConstructor = {
    new (view: PcvizBaseSceneView): PcvizScreenshotTool;
};

export interface PcvizSaveViewData {
    imageMode: boolean;
    isOrtho: boolean;
    sceneCenter: Vec3;
    poseEye: Vec3;
    poseTarget: Vec3;
}

export interface PcvizSaveView {
    name: string;
    viewData: PcvizSaveViewData;
}

export interface PcvizSaveViewTool extends PcvizTool {
    getSavedViews(): PcvizSaveView[];
    saveCurrentView(name: string): Promise<void>;
    renameView(index: number, name: string): Promise<void>;
    deleteView(index: number): Promise<void>;
    goToView(index: number): void;
}

export type PcvizSaveViewToolConstructor = {
    new (view: PcvizSceneView): PcvizSaveViewTool;
};
// eslint-disable-next-line @typescript-eslint/no-empty-interface, @typescript-eslint/no-empty-object-type
export interface PcvizVirtualTourTool extends PcvizTool {}

export type PcvizVirtualTourToolConstructor = {
    new (view: PcvizSceneView): PcvizVirtualTourTool;
};

export interface PcvizFlyToTool extends PcvizTool {
    setFlyParameters(position: Float64Array, target: Float64Array, duration: number, finishCallback: () => void): void;
}

export interface PcvizReferencePlanesTool extends PcvizTool {
    setActive(active: boolean, keepChildren?: boolean): void;
    createPolygonByPickPoint(pickPoint: any, flatPolygon: boolean): void;
    getPicksArray(): any[];
    clearReferencePlanes(value: boolean): void;
    setPicksArray(points: any[]): void;
    changeColor(color: any): void;
    registerPositionListener(fn: any): void;
    getFeature(): PcvizFeature;
    setMode(mode: number): void;
    setLayer(layer: PcvizLayer): void;
    updateReferencePlane(): void;
    hasReferencePlane(): boolean;
    createReferencePlaneByHeight(planeElevation: number): void;
    getPlaneAttributes(): PcvizReferencePlanesToolAttributes;
    clearArea(needsClearPicks: boolean): void;
    getOnlyComputePlane(): boolean;
    setOnlyComputePlane(flag: boolean): void;
    showPickingElements(flag: boolean): void;
    enableHighlightPoints(flag: boolean): void;
    clearPickingElements(): void;
}

export type PcvizReferencePlanesToolConstructor = {
    new (view: PcvizSceneView): PcvizReferencePlanesTool;
};

export type PcvizReferencePlanesToolAttributes = {
    centroid: Vec3;
    normal: Vec3;
    eigen1: Vec3;
    eigen2: Vec3;
    length: number;
    area: number;
    width: number;
    height: number;
};

export interface PcvizSidesTool extends PcvizTool {
    setSidesDividerValue(value: number): void;
    setSideByLayerID(layerId: number, side: number): void;
    invertSideSelections(): void;
    resetSides(): void;
    removeNodesFromSide(layerId: number): void;
    setPostChangeDividerFn(fn: () => void): void;
}

export type PcvizSidesToolConstructor = {
    new (view: PcvizJobSiteMapView | PcvizJobSiteSceneView): PcvizSidesTool;
};
